import type { Slide, SlideSuggestionCategory } from 'src/content/screens/slide-element';
import { t } from 'src/i18n';

// Skjermforslag merket med `audience: 'doctors'` (enten på kategorien eller
// på den enkelte slide) er kun relevante for legevirksomheter. Øvrige
// grupper skal få så mange forslag som mulig, men ikke det rent
// lege-spesifikke innholdet. Når `isDoctor` er true returneres alt.
export function slideSuggestionsCategorizedFor(isDoctor: boolean): SlideSuggestionCategory[] {
  if (isDoctor) return slideSuggestionsCategorized;
  return slideSuggestionsCategorized
    .filter((category) => category.audience !== 'doctors')
    .map((category) => ({
      ...category,
      slides: category.slides.filter((slide) => slide.audience !== 'doctors'),
    }));
}

const klokeValgPath = 'std/kloke-valg/';

// Bygger forslagslista. Wrappet i en funksjon (i stedet for en ren modul-const
// med innebygde t()-kall) slik at oversettelsene løses opp i kalltidspunktet
// og ikke før i18n er initialisert.
function buildSlideSuggestionsCategorized(): SlideSuggestionCategory[] {
  return [
    {
      name: t('screens.suggestionsCategoryFeeds'),
      id: 'feeds',
      description: t('screens.suggestionsCategoryFeedsDescription'),
      slides: [
        {
          type: 'fhi',
          id: 'fhi',
          name: t('screens.suggestionsSlideFhi'),
          // FHI = Folkehelseinstituttet. Helse-/folkehelsestoff er kun relevant
          // for legevirksomheter; følger samme audience-filter som resten (#13227).
          audience: 'doctors',
          duration: 15,
          itemsCount: 3,
          itemDuration: 5,
        },
        {
          type: 'news',
          id: 'news',
          name: t('screens.suggestionsSlideNews'),
          duration: 15,
          itemsCount: 3,
          itemDuration: 5,
          assets: ['std/news-default/green-defocus-vr28j4wj73.webp'],
        },
        {
          type: 'weather',
          id: 'weather',
          name: t('screens.suggestionsSlideWeather'),
          duration: 15,
          countyCode: '03',
          municipalCode: '0301',
          latitude: 59.972,
          longitude: 10.776,
        },
      ],
    },
    {
      name: t('screens.suggestionsCategoryTemplates'),
      description: t('screens.suggestionsCategoryTemplatesDescription'),
      descriptionLink: { href: 'mailto:post@trinnvis.no', text: t('screens.suggestionsSendUsEmail') },
      id: 'custom',
      // Hele kategorien er rettet mot legevirksomheter (malene bruker
      // legekontor-språk: legetime, resepsjon, blodprøver, resepter, priser).
      audience: 'doctors',
      slides: [
        {
          type: 'custom',
          id: 'welcome',
          name: t('screens.suggestionsSlideWelcomeName'),
          duration: 10,
          header: t('screens.suggestionsSlideWelcomeHeader'),
          text: t('screens.suggestionsSlideWelcomeText'),
          media: 'std/istock-2063599907.webp',
          assets: ['std/istock-2063599907.webp'],
          layout: {
            type: 'divider',
            orientation: 'horizontal',
            firstDivisionSize: 43.77575877582785,
            first: {
              type: 'media',
              left: -72.28153961057404,
              top: -0.24314168529115604,
              scale: 100.24355252164148,
            },
            last: {
              type: 'divider',
              orientation: 'vertical',
              firstDivisionSize: 16.191435190870315,
              first: {
                type: 'header',
              },
              last: {
                type: 'text',
              },
            },
          },
        },
        {
          type: 'custom',
          id: 'digital-contact',
          name: t('screens.suggestionsSlideDigitalContactName'),
          audience: 'doctors',
          duration: 10,
          header: t('screens.suggestionsSlideDigitalContactHeader'),
          text: t('screens.suggestionsSlideDigitalContactText'),
          media: 'std/istock-1975302922.webp',
          assets: ['std/istock-1975302922.webp'],
          layout: {
            type: 'divider',
            orientation: 'horizontal',
            firstDivisionSize: 50,
            first: {
              type: 'divider',
              orientation: 'vertical',
              firstDivisionSize: 19.41789748045178,
              first: {
                type: 'header',
              },
              last: {
                type: 'text',
              },
            },
            last: {
              type: 'media',
              left: -55.38662033014769,
              top: -2.4712810116806643,
              scale: 103,
            },
          },
        },
        {
          type: 'employees',
          id: 'employees',
          name: t('screens.suggestionsSlideEmployees'),
          duration: 5,
          doctorsSeparately: true,
          hide: [],
        },
        {
          type: 'custom',
          id: 'mask',
          name: t('screens.suggestionsSlideMaskName'),
          duration: 5,
          header: t('screens.suggestionsSlideMaskHeader'),
          text: t('screens.suggestionsSlideMaskText'),
          media: 'std/istock-1267039069.webp',
          assets: ['std/istock-1267039069.webp'],
          layout: {
            type: 'divider',
            orientation: 'vertical',
            firstDivisionSize: 71.94259567387688,
            first: {
              type: 'media',
              left: -0.5134671381031614,
              top: -1.6201502082581747,
              scale: 169,
            },
            last: {
              type: 'divider',
              orientation: 'vertical',
              firstDivisionSize: 49.38580705735075,
              first: {
                type: 'header',
              },
              last: {
                type: 'text',
              },
            },
          },
        },
        {
          type: 'custom',
          id: 'online-prescription',
          name: t('screens.suggestionsSlidePrescriptionName'),
          audience: 'doctors',
          duration: 10,
          header: t('screens.suggestionsSlidePrescriptionHeader'),
          text: t('screens.suggestionsSlidePrescriptionText'),
          media: 'std/istock-2263708092.webp',
          assets: ['std/istock-2263708092.webp'],
          layout: {
            type: 'divider',
            orientation: 'horizontal',
            firstDivisionSize: 42.09650582362729,
            first: {
              type: 'divider',
              orientation: 'vertical',
              firstDivisionSize: 34.2080329081161,
              first: {
                type: 'header',
              },
              last: {
                type: 'text',
              },
            },
            last: {
              type: 'media',
              left: -36.56384698275862,
              top: -2.766222961730449,
              scale: 103,
            },
          },
        },
        {
          type: 'custom',
          id: 'prices',
          name: t('screens.suggestionsSlidePricesName'),
          audience: 'doctors',
          duration: 10,
          header: t('screens.suggestionsSlidePricesHeader'),
          text: t('screens.suggestionsSlidePricesText'),
          media: 'std/rh096.webp',
          assets: ['std/rh096.webp'],
          layout: {
            type: 'divider',
            orientation: 'horizontal',
            firstDivisionSize: 24.069000133542705,
            first: {
              type: 'media',
              left: -251.5095278181679,
              top: 0,
              scale: 100,
            },
            last: {
              type: 'divider',
              orientation: 'vertical',
              firstDivisionSize: 17.165834719911256,
              first: {
                type: 'header',
              },
              last: {
                type: 'text',
              },
            },
          },
        },
      ],
    },
    {
      name: t('screens.suggestionsCategoryUserSlides'),
      id: 'user-slides',
      description: t('screens.suggestionsCategoryUserSlidesDescription'),
      slides: [],
    },
    {
      name: 'Trinnvis',
      id: 'trinnvis',
      description: '',
      // Kun relevant for legevirksomheter (pasientjournal-rettigheter o.l.).
      audience: 'doctors',
      slides: [
        {
          type: 'image',
          id: 'medical-records-rights',
          name: t('screens.suggestionsSlideMedicalRecordsRights'),
          duration: 10,
          media: 'std/trinnvis/journalrettigheter.webp',
          assets: ['std/trinnvis/journalrettigheter.webp'],
        },
        {
          type: 'image',
          id: 'we-use-trinnvis',
          name: t('screens.suggestionsSlideWeUseTrinnvis'),
          duration: 10,
          media: 'std/trinnvis/vi-bruker-trinnvis.webp',
          assets: ['std/trinnvis/vi-bruker-trinnvis.webp'],
        },
      ],
    },
    {
      name: t('screens.suggestionsCategoryWiseChoices'),
      id: 'wise-choices',
      description: t('screens.suggestionsCategoryWiseChoicesDescription'),
      descriptionLink: {
        href: 'https://www.legeforeningen.no/kloke-valg/',
        text: t('screens.suggestionsReadMoreCampaign'),
      },
      audience: 'doctors',
      slides: [
        {
          type: 'image',
          id: 'a',
          name: t('screens.suggestionsWiseChoiceA'),
          duration: 10,
          media: klokeValgPath + 'aldersrelaterte-forandringer-i-ledd-og-rygg.png',
          assets: [klokeValgPath + 'aldersrelaterte-forandringer-i-ledd-og-rygg.png'],
        },
        {
          type: 'image',
          id: 'b',
          name: t('screens.suggestionsWiseChoiceB'),
          duration: 10,
          media: klokeValgPath + 'antibiotika-nyfodte.png',
          assets: [klokeValgPath + 'antibiotika-nyfodte.png'],
        },
        {
          type: 'image',
          id: 'c',
          name: t('screens.suggestionsWiseChoiceC'),
          duration: 10,
          media: klokeValgPath + 'arbeid-er-helsefremmende.png',
          assets: [klokeValgPath + 'arbeid-er-helsefremmende.png'],
        },
        {
          type: 'image',
          id: 'd',
          name: t('screens.suggestionsWiseChoiceD'),
          duration: 10,
          media: klokeValgPath + 'bakterier-i-urinen.png',
          assets: [klokeValgPath + 'bakterier-i-urinen.png'],
        },
        {
          type: 'image',
          id: 'e',
          name: t('screens.suggestionsWiseChoiceE'),
          duration: 10,
          media: klokeValgPath + 'blodprover.png',
          assets: [klokeValgPath + 'blodprover.png'],
        },
        {
          type: 'image',
          id: 'f',
          name: t('screens.suggestionsWiseChoiceF'),
          duration: 10,
          media: klokeValgPath + 'eggstokkreft.png',
          assets: [klokeValgPath + 'eggstokkreft.png'],
        },
        {
          type: 'image',
          id: 'g',
          name: t('screens.suggestionsWiseChoiceG'),
          duration: 10,
          media: klokeValgPath + 'gulping-barn.png',
          assets: [klokeValgPath + 'gulping-barn.png'],
        },
        {
          type: 'image',
          id: 'h',
          name: t('screens.suggestionsWiseChoiceH'),
          duration: 10,
          media: klokeValgPath + 'helseundersokelser.png',
          assets: [klokeValgPath + 'helseundersokelser.png'],
        },
        {
          type: 'image',
          id: 'i',
          name: t('screens.suggestionsWiseChoiceI'),
          duration: 10,
          media: klokeValgPath + 'hjernerystelse.png',
          assets: [klokeValgPath + 'hjernerystelse.png'],
        },
        {
          type: 'image',
          id: 'j',
          name: t('screens.suggestionsWiseChoiceJ'),
          duration: 10,
          media: klokeValgPath + 'hodepine.png',
          assets: [klokeValgPath + 'hodepine.png'],
        },
        {
          type: 'image',
          id: 'k',
          name: t('screens.suggestionsWiseChoiceK'),
          duration: 10,
          media: klokeValgPath + 'hormonprover-kvinner.png',
          assets: [klokeValgPath + 'hormonprover-kvinner.png'],
        },
        {
          type: 'image',
          id: 'l',
          name: t('screens.suggestionsWiseChoiceL'),
          duration: 10,
          media: klokeValgPath + 'hostedempende-hos-barn.png',
          assets: [klokeValgPath + 'hostedempende-hos-barn.png'],
        },
        {
          type: 'image',
          id: 'm',
          name: t('screens.suggestionsWiseChoiceM'),
          duration: 10,
          media: klokeValgPath + 'langvarige-smerter.png',
          assets: [klokeValgPath + 'langvarige-smerter.png'],
        },
        {
          type: 'image',
          id: 'n',
          name: t('screens.suggestionsWiseChoiceN'),
          duration: 10,
          media: klokeValgPath + 'livmorhalsundersokelse-gravide.png',
          assets: [klokeValgPath + 'livmorhalsundersokelse-gravide.png'],
        },
        {
          type: 'image',
          id: 'o',
          name: t('screens.suggestionsWiseChoiceO'),
          duration: 10,
          media: klokeValgPath + 'magevondt-hos-barn.png',
          assets: [klokeValgPath + 'magevondt-hos-barn.png'],
        },
        {
          type: 'image',
          id: 'p',
          name: t('screens.suggestionsWiseChoiceP'),
          duration: 10,
          media: klokeValgPath + 'matvareallergi.png',
          assets: [klokeValgPath + 'matvareallergi.png'],
        },
        {
          type: 'image',
          id: 'q',
          name: t('screens.suggestionsWiseChoiceQ'),
          duration: 10,
          media: klokeValgPath + 'muskel-og-skjelettsmerter-arsak.png',
          assets: [klokeValgPath + 'muskel-og-skjelettsmerter-arsak.png'],
        },
        {
          type: 'image',
          id: 'r',
          name: t('screens.suggestionsWiseChoiceR'),
          duration: 10,
          media: klokeValgPath + 'myomer-livmor.png',
          assets: [klokeValgPath + 'myomer-livmor.png'],
        },
        {
          type: 'image',
          id: 's',
          name: t('screens.suggestionsWiseChoiceS'),
          duration: 10,
          media: klokeValgPath + 'smerter-foran-i-kneet.png',
          assets: [klokeValgPath + 'smerter-foran-i-kneet.png'],
        },
        {
          type: 'image',
          id: 't',
          name: t('screens.suggestionsWiseChoiceT'),
          duration: 10,
          media: klokeValgPath + 'ultralyd-gravide.png',
          assets: [klokeValgPath + 'ultralyd-gravide.png'],
        },
        {
          type: 'image',
          id: 'u',
          name: t('screens.suggestionsWiseChoiceU'),
          duration: 10,
          media: klokeValgPath + 'vondt-i-korsryggen.png',
          assets: [klokeValgPath + 'vondt-i-korsryggen.png'],
        },
        {
          type: 'image',
          id: 'v',
          name: t('screens.suggestionsWiseChoiceV'),
          duration: 10,
          media: klokeValgPath + 'orebetennelse-barn.png',
          assets: [klokeValgPath + 'orebetennelse-barn.png'],
        },
        {
          type: 'image',
          id: 'w',
          name: t('screens.suggestionsWiseChoiceW'),
          duration: 10,
          media: klokeValgPath + 'areknuter.png',
          assets: [klokeValgPath + 'areknuter.png'],
        },
      ],
    },
  ];
}

export const slideSuggestionsCategorized: SlideSuggestionCategory[] = buildSlideSuggestionsCategorized();

export function slideSuggestionsSelected(isDoctor: boolean): Slide[] {
  const items = [
    // FHI-nyheter er merket `audience: 'doctors'` og faller dermed ut av den
    // filtrerte kategorilista for ikke-leger (#13227).
    { category: 'feeds', slide: 'fhi' },
    { category: 'feeds', slide: 'news' },
    { category: 'feeds', slide: 'weather' },
    { category: 'custom', slide: 'welcome' },
    { category: 'custom', slide: 'employees' },
    { category: 'custom', slide: 'digital-contact' },
    { category: 'custom', slide: 'mask' },
    { category: 'wise-choices', slide: 'a' },
    { category: 'wise-choices', slide: 'p' },
  ];
  // Bygg fra den filtrerte kategorilista slik at lege-spesifikke forslag
  // (f.eks. digital-contact og kloke valg-plakatene) faller ut av default-
  // utvalget for ikke-leger.
  const categories = slideSuggestionsCategorizedFor(isDoctor);
  const result: Slide[] = [];
  const selected = (category: string, slide: string) => {
    const cat = categories.find((c) => c.id === category);
    if (cat) {
      return cat.slides.find((s) => s.id === slide);
    }
  };
  items.forEach((item) => {
    const slide = selected(item.category, item.slide);
    if (slide) {
      result.push(slide);
    }
  });
  return result;
}
